import React from 'react';
import {
    Redirect,
    Route,
    RouteComponentProps,
    Router,
    Switch,
} from 'react-router-dom';
import core, { App as AppTypes } from '@gin-betting/core';

import { ExtendedRouts } from 'app-constants';
import { ScrollToTop } from 'components';
import { AsyncMatchWithErrorBoundary } from 'modules/match/async';
import promoModule from 'modules/promo';
import tournamentModule from 'modules/tournament';
import FavoriteList from 'views/FavoriteList';
import Live from 'views/Live';
import Outright from 'views/Outright';
import Outrights from 'views/Outrights';
import Prematch from 'views/Prematch';
import Results from 'views/Results';
import Upcoming from 'views/Upcoming';
import App from '../root/App';
import EntityIdResolver from './EntityIdResolver';
import EntityIdRouteResolver from './EntityIdRouteResolver';

const {
    services: { getHistory },
} = core;
const {
    components: { TournamentComponent },
} = tournamentModule;
const {
    components: { FeaturedTournamentPage },
} = promoModule;

function applyBrowserLocationBlocker(history: ReturnType<typeof getHistory>) {
    let currentLocation: string | null = null;

    history.block((location, action) => {
        const nextLocation =
            location.pathname + location.search + location.hash;

        if (action === 'PUSH') {
            if (currentLocation === nextLocation) {
                return false;
            }
        }

        currentLocation = nextLocation;
    });
}

const InnerRouts: React.FC<RouteComponentProps> = ({
    match: { path },
}): JSX.Element => {
    return (
        <Switch>
            <Route
                exact={true}
                path={`${path}${AppTypes.Routes.UPCOMING_LIST}`}
                component={Upcoming}
            />
            <Route
                path={`${path}${AppTypes.Routes.OUTRIGHTS_LIST}`}
                component={Outrights}
            />
            <Route
                path={`${path}${AppTypes.Routes.RESULTS_LIST}`}
                component={Results}
            />
            <Route
                exact={true}
                path={`${path}${AppTypes.Routes.LIVE_LIST}`}
                component={Live}
            />
            <Route
                path={`${path}${AppTypes.Routes.PREMATCH}`}
                component={Prematch}
            />
            <Route
                path={`${path}${AppTypes.Routes.OUTRIGHT}:slug`}
                render={props => (
                    <EntityIdResolver
                        sportEventId={props.match.params.slug}
                        slug={props.match.params.slug}
                    >
                        {sportEventId => (
                            <Outright matchId={sportEventId} {...props} />
                        )}
                    </EntityIdResolver>
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.RESULT_MATCH}:slug`}
                render={props => (
                    <AsyncMatchWithErrorBoundary
                        slugOrId={props.match.params.slug}
                    />
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.MATCH}:slug`}
                render={props => (
                    <AsyncMatchWithErrorBoundary
                        slugOrId={props.match.params.slug}
                    />
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.TOURNAMENT}:slug`}
                render={props => (
                    <EntityIdResolver
                        slug={props.match.params.slug}
                        tournamentId={props.match.params.slug}
                    >
                        {tournamentId => (
                            <TournamentComponent tournamentId={tournamentId} />
                        )}
                    </EntityIdResolver>
                )}
            />
            <Route
                exact={true}
                path={`${path}/:slug`}
                component={FeaturedTournamentPage}
            />
            <Redirect to={path} />
        </Switch>
    );
};

export default function createRoutes(): JSX.Element {
    const history = getHistory();
    applyBrowserLocationBlocker(history);

    return (
        <Router history={history}>
            <ScrollToTop>
                <App>
                    <Switch>
                        <Route
                            exact={true}
                            path={AppTypes.Routes.UPCOMING_LIST}
                            component={Upcoming}
                        />
                        <Route
                            path={AppTypes.Routes.OUTRIGHTS_LIST}
                            component={Outrights}
                        />
                        <Route
                            path={AppTypes.Routes.RESULTS_LIST}
                            component={Results}
                        />
                        <Route
                            exact={true}
                            path={AppTypes.Routes.LIVE_LIST}
                            component={Live}
                        />
                        <Route
                            path={AppTypes.Routes.PREMATCH}
                            component={Prematch}
                        />
                        <Route
                            path={`${AppTypes.Routes.MATCH}:slug`}
                            render={props => (
                                <EntityIdRouteResolver
                                    slug={props.match.params.slug}
                                    path={AppTypes.Routes.MATCH}
                                />
                            )}
                        />
                        <Route
                            path={`${AppTypes.Routes.TOURNAMENT}:slug`}
                            render={props => (
                                <EntityIdRouteResolver
                                    slug={props.match.params.slug}
                                    path={AppTypes.Routes.TOURNAMENT}
                                />
                            )}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_UPCOMING}
                            component={FavoriteList}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_OUTRIGHTS}
                            component={FavoriteList}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_RESULTS}
                            component={FavoriteList}
                        />
                        <Route
                            path={AppTypes.Routes.CLASSIC}
                            component={InnerRouts}
                        />
                        <Route
                            path={AppTypes.Routes.ESPORT}
                            component={InnerRouts}
                        />
                        <Redirect to={AppTypes.Routes.UPCOMING_LIST} />
                    </Switch>
                </App>
            </ScrollToTop>
        </Router>
    );
}
